<script setup>
import { computed, nextTick, reactive, ref, watch } from 'vue';

/**
 * LaisTabs — the single tabs contract for every LAIS host.
 *
 * Data-driven (no domain rules): the parent passes an array of tab descriptors
 * and owns route/query sync + permission gating. A restricted tab must simply
 * be left out of `tabs` by the parent BEFORE the request, so its panel never
 * mounts. This component owns semantics, keyboard and lazy/cached panels only.
 *
 * Tab descriptor: { id, label, icon?, badge?, disabled? }
 * Panels are provided as named slots keyed by tab id (`#<id>`), with a scoped
 * `#panel` fallback that receives `{ tab }`.
 *
 * v-model holds the active tab id. Parents typically bind it to a route query.
 */
const props = defineProps({
  tabs: {
    type: Array,
    default: () => [],
    validator: value =>
      value.every(
        tab => tab && typeof tab.id !== 'undefined' && 'label' in tab
      ),
  },
  modelValue: { type: [String, Number], default: '' },
  ariaLabel: { type: String, default: '' },
  // Mount a panel only the first time it becomes active, then keep it cached.
  lazy: { type: Boolean, default: true },
  // Visually stretch triggers to fill the row (good for 2-3 tabs on mobile).
  fill: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'change']);

const instanceId = `lais-tabs-${Math.random().toString(36).slice(2, 8)}`;
const tabRefs = ref([]);
const mountedTabs = reactive(new Set());

const enabledTabs = computed(() => props.tabs.filter(tab => !tab.disabled));

const activeId = computed(() => {
  const current = props.tabs.find(
    tab => String(tab.id) === String(props.modelValue)
  );
  if (current && !current.disabled) return current.id;
  return enabledTabs.value[0]?.id ?? '';
});

const tabId = id => `${instanceId}-tab-${id}`;
const panelId = id => `${instanceId}-panel-${id}`;

const isActive = id => String(id) === String(activeId.value);

const shouldRenderPanel = id => {
  if (!props.lazy) return true;
  return isActive(id) || mountedTabs.has(String(id));
};

const badgeLabel = value => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number' && value <= 0) return '';
  return typeof value === 'number' && value > 99 ? '99+' : String(value);
};

const selectTab = id => {
  const target = props.tabs.find(tab => String(tab.id) === String(id));
  if (!target || target.disabled) return;
  mountedTabs.add(String(id));
  if (String(id) !== String(props.modelValue)) {
    emit('update:modelValue', id);
    emit('change', id);
  }
};

const focusTabAt = index => {
  const el = tabRefs.value[index];
  if (el) el.focus();
};

const enabledIndexOf = id =>
  enabledTabs.value.findIndex(tab => String(tab.id) === String(id));

const moveFocus = (fromId, delta) => {
  const list = enabledTabs.value;
  if (!list.length) return;
  const currentIndex = Math.max(0, enabledIndexOf(fromId));
  const nextIndex = (currentIndex + delta + list.length) % list.length;
  const nextTab = list[nextIndex];
  selectTab(nextTab.id);
  nextTick(() => {
    const domIndex = props.tabs.findIndex(
      tab => String(tab.id) === String(nextTab.id)
    );
    focusTabAt(domIndex);
  });
};

const onKeydown = (event, tab) => {
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      event.preventDefault();
      moveFocus(tab.id, 1);
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      event.preventDefault();
      moveFocus(tab.id, -1);
      break;
    case 'Home':
      event.preventDefault();
      if (enabledTabs.value[0]) {
        selectTab(enabledTabs.value[0].id);
        nextTick(() => focusTabAt(0));
      }
      break;
    case 'End': {
      event.preventDefault();
      const last = enabledTabs.value[enabledTabs.value.length - 1];
      if (last) {
        selectTab(last.id);
        nextTick(() => {
          const domIndex = props.tabs.findIndex(
            t => String(t.id) === String(last.id)
          );
          focusTabAt(domIndex);
        });
      }
      break;
    }
    case 'Enter':
    case ' ':
      event.preventDefault();
      selectTab(tab.id);
      break;
    default:
      break;
  }
};

// Keep the active id valid and cached whenever inputs change.
watch(
  activeId,
  id => {
    if (id !== '' && id !== undefined) mountedTabs.add(String(id));
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex min-w-0 flex-col gap-4">
    <div
      role="tablist"
      :aria-label="ariaLabel || undefined"
      class="flex gap-1.5 overflow-x-auto border-b border-lais-border-soft pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <button
        v-for="tab in tabs"
        :id="tabId(tab.id)"
        :key="tab.id"
        ref="tabRefs"
        type="button"
        role="tab"
        :aria-selected="isActive(tab.id) ? 'true' : 'false'"
        :aria-controls="panelId(tab.id)"
        :aria-disabled="tab.disabled ? 'true' : undefined"
        :tabindex="isActive(tab.id) ? 0 : -1"
        :disabled="tab.disabled"
        class="inline-flex h-10 shrink-0 items-center gap-2 rounded-lais px-3.5 text-lais-label-sm font-semibold outline-none transition focus-visible:shadow-lais-focus disabled:cursor-not-allowed disabled:opacity-50"
        :class="[
          fill ? 'flex-1 justify-center' : '',
          isActive(tab.id)
            ? 'bg-lais-primary text-white shadow-lais-card'
            : 'bg-lais-bg-card text-lais-text-muted hover:bg-lais-primary-50 hover:text-lais-primary',
        ]"
        @click="selectTab(tab.id)"
        @keydown="onKeydown($event, tab)"
      >
        <span
          v-if="tab.icon"
          class="size-4 shrink-0"
          :class="tab.icon"
          aria-hidden="true"
        />
        <span class="truncate">{{ tab.label }}</span>
        <span
          v-if="badgeLabel(tab.badge)"
          class="grid h-4 min-w-4 place-items-center rounded-full px-1 text-lais-label-xs font-bold tabular-nums"
          :class="
            isActive(tab.id)
              ? 'bg-white/20 text-white'
              : 'bg-lais-bg-soft text-lais-text-muted'
          "
        >
          {{ badgeLabel(tab.badge) }}
        </span>
      </button>
    </div>

    <template v-for="tab in tabs" :key="`panel-${tab.id}`">
      <div
        v-if="shouldRenderPanel(tab.id)"
        v-show="isActive(tab.id)"
        :id="panelId(tab.id)"
        role="tabpanel"
        :aria-labelledby="tabId(tab.id)"
        tabindex="0"
        class="min-w-0 outline-none focus-visible:shadow-lais-focus"
      >
        <slot :name="tab.id" :tab="tab">
          <slot name="panel" :tab="tab" />
        </slot>
      </div>
    </template>
  </div>
</template>
